import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { NextRouter } from 'next/router';
import { GET_CLIENT_DIRECTORY } from '@/api';
import { lawyerDirectoryService } from '@/api';
import { CaseClientsResponse } from '@/interfaces';
import { useCaseClientFilters } from './useCaseClientFilters';

type PaginationMode = 'page' | 'infinite';

interface UseCaseClientProps {
	router: NextRouter;
	paginationMode: PaginationMode;
	stepNotification?: string | null;
}

export const useCaseClient = ({
	router,
	paginationMode,
	stepNotification,
}: UseCaseClientProps) => {
	const isInfinite = paginationMode === 'infinite';

	const LIMIT = 7;

	const { id, page } = router.query;

	const { rawFilters, parsedFilters } = useCaseClientFilters();

	/**
	 * 🔵 Infinite pagination (scroll / load more)
	 */

	const fetchData = async ({ pageParam = 1 }): Promise<CaseClientsResponse> => {
		const response = await lawyerDirectoryService.getCaseClients({
			...parsedFilters,
			limit: LIMIT,
			clientId: rawFilters.clientId,
			segmentId: rawFilters.segmentId,
			isOrderAlph: !!rawFilters.isOrderAlph,
			page: pageParam,
			caseId: id as string,
			totalCase: rawFilters.totalCase,
			totalTaskClient: rawFilters.totalTaskClient,
			totalTaskLawyer: rawFilters.totalTaskLawyer,
			actionStatusLawyer: rawFilters.actionStatusLawyer,
			actionStatusClient: rawFilters.actionStatusClient,
			totalMessage: rawFilters.totalMessage,
		});

		return response.data;
	};

	const infiniteQuery = useInfiniteQuery({
		enabled: isInfinite,
		queryKey: [
			'GET_CLIENT_DIRECTORY_INFINITY',
			paginationMode,
			id,
			stepNotification,
			...Object.values(rawFilters),
		],
		initialData: undefined,
		initialPageParam: 1,
		queryFn: fetchData,
		getNextPageParam: (lastPage: CaseClientsResponse) =>
			lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
	});

	/**
	 * 🟢 Page-based pagination (URL-driven)
	 */
	const pageQuery = useQuery({
		enabled: !isInfinite,
		queryKey: [
			GET_CLIENT_DIRECTORY,
			paginationMode,
			page,
			id,
			stepNotification,
			...Object.values(rawFilters),
		],
		queryFn: () =>
			lawyerDirectoryService.getCaseClients({
				...parsedFilters,
				limit: LIMIT,
				clientId: rawFilters.clientId,
				segmentId: rawFilters.segmentId,
				isOrderAlph: !!rawFilters.isOrderAlph,
				page: page ? Number(page) : 1,
				caseId: id as string,
				totalCase: rawFilters.totalCase,
				totalTaskClient: rawFilters.totalTaskClient,
				totalTaskLawyer: rawFilters.totalTaskLawyer,
				actionStatusLawyer: rawFilters.actionStatusLawyer,
				actionStatusClient: rawFilters.actionStatusClient,
				totalMessage: rawFilters.totalMessage,
			}),
	});

	/**
	 * 🔁 Normalized data
	 */
	const items = isInfinite
		? infiniteQuery.data?.pages.flatMap((p) => p.items) ?? []
		: pageQuery.data?.data.items ?? [];

	const totalItems = isInfinite
		? infiniteQuery.data?.pages[0]?.totalItems ?? 0
		: pageQuery.data?.data.totalItems ?? 0;

	const totalPages = isInfinite
		? infiniteQuery.data?.pages[0]?.totalPages ?? 0
		: pageQuery.data?.data.totalPages ?? 0;

	return {
		items,
		totalItems,
		totalPages,
		limit: LIMIT,
		rawResponse: isInfinite
			? infiniteQuery.data?.pages.at(-1)
			: pageQuery.data?.data,
		isLoading: isInfinite ? infiniteQuery.isLoading : pageQuery.isLoading,
		isFetching: isInfinite ? infiniteQuery.isFetching : pageQuery.isFetching,
		hasNextPage: isInfinite ? infiniteQuery.hasNextPage ?? false : false,
		fetchNextPage: isInfinite ? infiniteQuery.fetchNextPage : undefined,
		isFetchingNextPage: isInfinite ? infiniteQuery.isFetchingNextPage : false,
	};
};
